/**
 * @returns {Object} Environment configuration object
 */
export const getEnvConfig = () => {
  return (
    window._env_ || {
      VITE_APP_BASE_URL: "http://localhost:8001/api",
    }
  );
};

/**
 * Get API base URL from environment configuration
 * @returns {string} API base URL
 */
export const getApiBaseUrl = () => {
  const config = getEnvConfig();
  return config.VITE_APP_BASE_URL || "http://localhost:8001/api";
};

/**
 * Check if application is running in development mode
 * @returns {boolean} True if in development
 */
export const isDevelopment = () => {
  return import.meta.env.DEV;
};

/**
 * Check if application is running in production mode
 * @returns {boolean} True if in production
 */
export const isProduction = () => {
  return import.meta.env.PROD;
};

/**
 * Get current environment name
 * @returns {string} Environment name
 */
export const getEnvironment = () => {
  return import.meta.env.MODE || "development";
};

/**
 * SKEMA brand colors
 */
export const COLORS = {
  primary: "#003366",
  secondary: "#336699",
  accent: "#ff6b6b",
  lightBg: "#f8f9fa",
  lightText: "#ffffff",
  darkText: "#333333",
  border: "#e1e5e9",
  success: "#28a745",
  warning: "#ffc107",
  error: "#dc3545",
  info: "#17a2b8",
};

/**
 * Application configuration constants
 */
export const APP_CONFIG = {
  name: "ISCRI",
  version: "1.0.0",
  description: "Interstate Conflictual Relations Index",
  title: "ISCRI - Interstate Conflictual Relations Index",
};

/**
 * Get ISCRI end period based on the current date and a given end period.
 *
 * Context:
 * The ISCRI index for a given month is calculated on the 4th of the following month.
 * To provide a safe margin (e.g., accounting for data processing delays),
 * the logic considers the calculation ready only starting from the 6th.
 *
 * Logic:
 * - If today's date is on or after the 6th of the month → use the previous month's data.
 * - If it's before the 6th → use the data from two months ago.
 *
 * Example:
 * - On Sept 5, 2025 → still too early for August data → returns July 2025.
 * - On Sept 6, 2025 → August data is ready → returns August 2025.
 *
 * Handles year rollover correctly (e.g., from January back to December of the previous year).
 *
 * @param {number} endYear - The provided end year (e.g., 2025)
 * @param {number} endMonth - The provided end month (1–12, e.g., 10 for October)
 * @returns {{ endYear: number, endMonth: number }} The adjusted year and month
 */
export const getIscriEndPeriod = (endYear, endMonth) => {
  const today = new Date();
  const currentDay = today.getDate();

  let targetMonth;
  let targetYear;

  // Determine which month to use based on the current day
  if (currentDay >= 6) {
    // On or after the 6th → previous month's data is considered available
    targetMonth = endMonth - 1;
    targetYear = endYear;
  } else {
    // Before the 6th → previous month's data may not be ready yet
    targetMonth = endMonth - 2;
    targetYear = endYear;
  }

  // Handle rollover when subtracting months moves before January
  while (targetMonth <= 0) {
    targetMonth += 12;
    targetYear -= 1;
  }

  return {
    endYear: targetYear,
    endMonth: targetMonth,
  };
};

/**
 * Default ISCRI parameters
 */
export const ISCRI_DEFAULTS = {
  delta: 0.1,
  startYear: 2015,
  normalization: "none",
  availableNormalizations: ["none", "base_100", "deviation"],
  components: ["total", "verbal", "material"],
};

/**
 * Chart configuration defaults
 */
export const CHART_CONFIG = {
  defaultHeight: 500,
  defaultWidth: "100%",
  plotly: {
    displayModeBar: true,
    responsive: true,
    displaylogo: false,
    modeBarButtonsToRemove: ["pan2d", "lasso2d", "select2d"],
  },
};

/**
 * Responsive breakpoints
 */
export const BREAKPOINTS = {
  mobile: "768px",
  tablet: "1024px",
  desktop: "1200px",
};

/**
 * API endpoints paths
 */
export const API_ENDPOINTS = {
  countries: "/countries",
  iscri: "/iscri",
  dailyRisk: "/daily-risks",
  monthlyRisk: "/monthly-risks",
  visualizations: "/visualizations",
};

/**
 * Get default date parameters based on data type
 * @param {string} dataType - The data type ('iscri', 'monthly', 'daily')
 * @returns {Object} Default date parameters object
 */
export const getDefaultDateParams = (dataType) => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const adjustedEnd = getIscriEndPeriod(currentYear, currentMonth);

  switch (dataType) {
    case "iscri": {
      return {
        startYear: 2015,
        startMonth: 1,
        endYear: adjustedEnd.endYear,
        endMonth: adjustedEnd.endMonth,
        startDate: undefined,
        endDate: undefined,
      };
    }
    case "monthly": {
      return {
        startYear: 2020,
        startMonth: 1,
        endYear: adjustedEnd.endYear,
        endMonth: adjustedEnd.endMonth,
        startDate: undefined,
        endDate: undefined,
      };
    }
    case "daily": {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);

      return {
        startYear: undefined,
        startMonth: undefined,
        endYear: undefined,
        endMonth: undefined,
        startDate: startDate.toISOString().split("T")[0],
        endDate: endDate.toISOString().split("T")[0],
      };
    }
    default: {
      return {
        startYear: undefined,
        startMonth: undefined,
        endYear: undefined,
        endMonth: undefined,
        startDate: undefined,
        endDate: undefined,
      };
    }
  }
};

/**
 * Get complete application configuration
 * @returns {Object} Complete configuration object
 */
export const getConfig = () => {
  const envConfig = getEnvConfig();

  return {
    apiBaseUrl: getApiBaseUrl(),
    environment: getEnvironment(),
    isDevelopment: isDevelopment(),
    isProduction: isProduction(),
    ...envConfig,
    app: APP_CONFIG,
    iscri: ISCRI_DEFAULTS,
    charts: CHART_CONFIG,
    colors: COLORS,
    breakpoints: BREAKPOINTS,
    api: API_ENDPOINTS,
  };
};

/**
 * Log configuration details in development mode
 */
export const logConfig = () => {
  if (isDevelopment()) {
    const config = getConfig();
    console.log("🌐 Full Config:", config);
  }
};

